var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { RG_TYPE } from "@project-rouge/rg-core";
import { randomWeightedItem } from "@project-rouge/rg-three";
import { AggNmcsState } from "./index131.js";
import { getCoreCost } from "./index200.js";
import { getCorridorCost } from "./index201.js";
import { getEndClashCost } from "./index202.js";
import { getEntranceCost } from "./index203.js";
import { getFireCoreCost } from "./index204.js";
import { getLengthCost } from "./index205.js";
import { getLiftCoreCost } from "./index206.js";
import { getEfficiencyCost } from "./index207.js";
import { getUnitMixCost } from "./index208.js";
import { NmcsRules } from "./index133.js";
import { getStairCoreCost } from "./index209.js";
import { getDataAndActions } from "./index210.js";
import { getDoorDistanceFromRight } from "./index211.js";
import { getStartClashStatus } from "./index212.js";
import { getClosingCost } from "./index213.js";
class AggNmcsRules extends NmcsRules {
  constructor(aggRulesProps) {
    const { gap } = aggRulesProps.dataset.aggConfig;
    const { data, actions } = getDataAndActions(aggRulesProps);
    super(new AggNmcsState({ gap, data }));
    __publicField(this, "actions");
    __publicField(this, "data");
    this.data = data;
    this.actions = actions;
  }
  isActionValid(state, action, ignoreEntranceCheck) {
    const { gap } = this.data.dataset.aggConfig;
    const unitLength = gap + action.unit.size[0];
    if (!this.isValidForBuildingEntrance(state, action, ignoreEntranceCheck))
      return false;
    const totalLengthWithShift = state.totalLength + state.requiredShift;
    const prevUnit = state.sequence[state.sequence.length - 1];
    const isDefinitelyLastApartment = totalLengthWithShift + unitLength > this.data.targetLength - this.data.smallestApartmentSize;
    if (totalLengthWithShift + unitLength > this.data.targetLength)
      return false;
    if (state.sequence.length === 0 && !this.isValidForClashesAtStart(action, this.data.lockEnds[0]))
      return false;
    if (isDefinitelyLastApartment && !this.isValidForClashesAtEnd(state, action))
      return false;
    if (action.unit.type === RG_TYPE.Apartment && action.unit.data.closeDirection) {
      const closeDirection = action.unit.data.closeDirection;
      const canCloseStart = this.data.canCloseEnds[0] && this.data.endClosingDistances[0] === 0;
      const canCloseEnd = this.data.canCloseEnds[1] && this.data.endClosingDistances[1] === 0;
      const closesAtStart = closeDirection[0] > 0;
      const closesAtEnd = closeDirection[0] < 0;
      const isFirstUnit = state.sequence.length === 0;
      if (closesAtStart && !isFirstUnit)
        return false;
      if (closesAtStart && !canCloseStart)
        return false;
      if (closesAtEnd && !canCloseEnd)
        return false;
      if (closesAtEnd && !isDefinitelyLastApartment)
        return false;
    }
    if (prevUnit && prevUnit.unit.type === RG_TYPE.Apartment && prevUnit.unit.data.closeCorridor) {
      const closeDirection = prevUnit.unit.data.closeDirection;
      if (closeDirection && closeDirection[0] < 0)
        return false;
    }
    if (!this.isValidForSkipCores(action))
      return false;
    if (!this.isValidForFireCores(state, action))
      return false;
    if (!this.isValidForCorridorDistances(state, action, totalLengthWithShift, prevUnit, unitLength))
      return false;
    return true;
  }
  /** Checks related to building entrances, including:
   * - making sure there is an entrance at the start when required
   * - invalidating entrances when not required
   * - checking next to entrance validity
   * - checking above entrance validity
  */
  isValidForBuildingEntrance(state, action, ignoreEntranceCheck) {
    if (action.unit.type === RG_TYPE.Entrance) {
      if (state.entranceCount + 1 > this.data.targetEntranceCount)
        return false;
      const tempState = state.clone();
      tempState.update(action);
      const nextToActions = this.actions.filter((a) => a.unit.type === RG_TYPE.Apartment && a.unit.data.nextToEntrance);
      const validForNext = nextToActions.some((n) => this.isActionValid(tempState, n));
      if (!validForNext)
        return false;
      const aboveActions = this.actions.filter((a) => a.unit.type === RG_TYPE.Apartment && a.unit.data.aboveEntrance);
      const validForAbove = aboveActions.some((n) => this.isActionValid(state, n, true));
      if (!validForAbove)
        return false;
    }
    if (state.sequence.length === 0 && this.data.hasEntryAtStart && !ignoreEntranceCheck) {
      if (action.unit.type !== RG_TYPE.Entrance)
        return false;
    }
    let isRequiredByEntrance = false;
    const prevUnit = state.sequence[state.sequence.length - 1];
    const isPrevUnitEntrance = prevUnit && prevUnit.unit.type === RG_TYPE.Entrance;
    if (isPrevUnitEntrance) {
      const validAfterEntrance = this.data.dataset.apartments.nextToEntrance.map((a) => a.name).includes(action.unit.name);
      if (!validAfterEntrance) {
        return false;
      } else {
        isRequiredByEntrance = true;
      }
    }
    if (action.rolloutWeight === 0 && !isRequiredByEntrance) {
      return false;
    }
    return true;
  }
  /** loop through all actions and append valid actions to validActions list */
  getValidActions(state) {
    const allActions = this.actions;
    const validActions = [];
    for (const action of allActions) {
      if (this.isActionValid(state, action)) {
        validActions.push(action);
      }
    }
    return validActions;
  }
  /** Costs are used to optimise the action selection, the lower the cost the better,
   * in general each cost should not change too much even if the muscle length
   * changes considerably in order to bring consistency and make tuning weights easier.
  */
  getCost(state) {
    const corridorCost = this.data.weights.corridorLengthWeight * getCorridorCost(state, this.data);
    const coreCost = this.data.weights.coreCountWeight * getCoreCost(state, this.data);
    const lengthCost = this.data.weights.totalLengthWeight * getLengthCost(state, this.data);
    const unitMixCost = this.data.weights.unitMixWeight * getUnitMixCost(state, this.data);
    const entranceCost = this.data.weights.entranceCountWeight * getEntranceCost(state, this.data);
    const fireCoreCost = 1 * getFireCoreCost(state, this.data);
    const liftCoreCost = 1 * getLiftCoreCost(state, this.data);
    const stairCoreCost = 1 * getStairCoreCost(state, this.data);
    const efficiencyCost = this.data.weights.efficiencyWeight * getEfficiencyCost(state);
    const endClashCost = 1 * getEndClashCost(state, this.data);
    const closingCost = this.data.weights.closingWeight * getClosingCost(state, this.data);
    const totalCost = corridorCost + coreCost + lengthCost + unitMixCost + entranceCost + fireCoreCost + liftCoreCost + stairCoreCost + efficiencyCost + endClashCost + closingCost;
    return totalCost;
  }
  pickActionWithPolicy(validActions, rng) {
    if (validActions.length === 1) {
      return validActions[0];
    }
    const weights = [];
    for (const action2 of validActions)
      weights.push(action2.rolloutWeight);
    const action = randomWeightedItem({ list: validActions, weights, rng });
    return action;
  }
  cloneState(state) {
    return state.clone();
  }
  updateState(state, action) {
    state.update(action);
  }
  finalAction(state) {
    state.finalUpdate(this.data);
  }
  /** returns true if valid, false otherwise */
  isValidForClashesAtStart(action, lockedStart) {
    const startClosingDistance = this.data.endClosingDistances[0];
    const startEntranceDistance = this.data.endEntranceDistances[0];
    const skipExistingClosingCheck = !lockedStart;
    const clashStatus = getStartClashStatus({ action, startClosingDistance, startEntranceDistance, skipExistingClosingCheck });
    return clashStatus;
  }
  isValidForClashesAtEnd(state, action) {
    let shift = 0;
    if (!this.data.lockEnds[1]) {
      shift = this.data.targetLength - (state.totalLength + this.data.dataset.aggConfig.gap + action.unit.size[0]);
    }
    if (this.data.endClosingDistances[1] > 0) {
      const doorPositionFromEnd = getDoorDistanceFromRight(action) + shift;
      if (doorPositionFromEnd < this.data.endClosingDistances[1]) {
        return false;
      }
      if (action.unit.type === RG_TYPE.Apartment && action.unit.data.closeCorridor) {
        return false;
      }
    }
    if (action.unit.type === RG_TYPE.Apartment && action.unit.data.closeCorridor) {
      if (action.unit.data.closeLength + shift > this.data.endEntranceDistances[1]) {
        return false;
      }
    }
    return true;
  }
  isValidForCorridorDistances(state, action, totalLengthWithShift, prevUnit, unitLength) {
    if (this.data.skipCores)
      return true;
    const corridorDists = state.corridorLengths;
    const currentCorridorLength = corridorDists[corridorDists.length - 1] ?? 0;
    const lengthRemaining = this.data.targetLength - totalLengthWithShift;
    const { corridorType, maxCorridorDist } = this.getCorridorTypeAndDistance(corridorDists, lengthRemaining);
    if (action.unit.type === RG_TYPE.Core) {
      const twoConsecutiveCores = prevUnit && prevUnit.unit.type === RG_TYPE.Core;
      if (twoConsecutiveCores) {
        return false;
      }
    } else {
      if (!prevUnit && corridorType === "Start" && !this.data.hasCornerAt[0]) {
        if (Math.max(getDoorDistanceFromRight(action), unitLength - this.data.endEntranceDistances[0]) > maxCorridorDist) {
          return false;
        }
      } else if (corridorType === "End" && !this.data.hasCornerAt[1]) {
        if (currentCorridorLength + action.doorPosition > maxCorridorDist) {
          return false;
        }
      } else {
        if (currentCorridorLength + unitLength > maxCorridorDist) {
          return false;
        }
      }
    }
    return true;
  }
  isValidForSkipCores(action) {
    if (this.data.skipCores && action.unit.type === RG_TYPE.Core) {
      return false;
    }
    return true;
  }
  isValidForFireCores(state, action) {
    if (action.unit.type === RG_TYPE.Core) {
      if (action.unit.data.hasFireLift) {
        if (state.fireCoreCount + 1 > this.data.targetFireCoreCount) {
          return false;
        }
      }
      if (action.unit.data.hasNonFireLift) {
        if (state.liftCoreCount + 1 > this.data.targetLiftCoreCount) {
          return false;
        }
      }
    }
    return true;
  }
  /** `corridorType` is calculated and defined as follows:
   * - Start: the current unit is positioned before the first core
   * - Middle: the current unit is between two cores
   * - End: the current unit is after the last core
   *
   * the `maxCorridorDist` reflects the `corridorType` and `aggConfig`
   */
  getCorridorTypeAndDistance(corridorDists, lengthRemaining) {
    let corridorType;
    const allowableEndDist = this.data.maxCorridorDists.end - this.data.distToCore[1];
    const currentEndCorridorLength = lengthRemaining + corridorDists[corridorDists.length - 1];
    if (currentEndCorridorLength <= allowableEndDist + this.data.minDoorPositionFromEnd) {
      corridorType = "End";
    } else if (corridorDists.length <= 1) {
      corridorType = "Start";
    } else {
      corridorType = "Middle";
    }
    let maxCorridorDist;
    if (corridorType === "Start")
      maxCorridorDist = this.data.maxCorridorDists.start;
    else if (corridorType === "Middle")
      maxCorridorDist = this.data.maxCorridorDists.middle;
    else
      maxCorridorDist = allowableEndDist;
    return { corridorType, maxCorridorDist };
  }
}
export {
  AggNmcsRules
};
//# sourceMappingURL=index130.js.map
